/**
 * `/kb` — the workspaces index, a card grid. Each card links into its workspace
 * and shows the type chips + docs/chunks footer (per-workspace stats from the
 * existing stats endpoint). A dashed "New workspace" tile + header button reveal
 * an inline create form.
 */
import { Button } from "@d12labs/ui/button";
import { ConfirmDialog } from "@d12labs/ui/confirm-dialog";
import { EmptyState } from "@d12labs/ui/empty-state";
import { Input } from "@d12labs/ui/input";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { Skeleton } from "@d12labs/ui/skeleton";
import { Toolbar } from "@d12labs/ui/toolbar";
import { Link } from "@tanstack/react-router";
import { Library, Plus, Search, Trash2 } from "lucide-react";
import { useState } from "react";
import {
	useCreateWorkspace,
	useDeleteWorkspace,
	useWorkspaceStats,
	useWorkspaces,
} from "../hooks/use-kb";
import { DOC_TYPE_META, timeAgo } from "../kb-ui";
import type { DocType, KbWorkspace } from "../types";

function WorkspaceCard({ workspace, onDelete }: { workspace: KbWorkspace; onDelete: () => void }) {
	const { data: stats } = useWorkspaceStats(workspace.id);
	const types = stats
		? (Object.entries(stats.byType) as [DocType, number][]).filter(([, n]) => n > 0)
		: [];

	return (
		<div className="group relative flex flex-col rounded-lg border border-hairline bg-surface-1 p-4 transition-colors hover:border-hairline-strong hover:bg-surface-2/50">
			{/* Full-card navigation overlay (keeps the delete button clickable above it). */}
			<Link
				to="/kb/$workspaceId"
				params={{ workspaceId: workspace.id }}
				aria-label={workspace.name}
				className="absolute inset-0 rounded-lg"
			/>

			<div className="pointer-events-none relative flex flex-1 flex-col">
				<div className="mb-2.5 flex items-center gap-2.5">
					<span className="flex size-8 flex-none items-center justify-center rounded-md border border-hairline bg-surface-2 text-primary-hover">
						<Library className="size-4" />
					</span>
					<span className="flex-1 truncate text-body font-medium text-foreground">
						{workspace.name}
					</span>
				</div>
				<p className="flex-1 text-body-sm leading-snug text-ink-subtle">
					Grouped document set — ask grounded questions across its indexed sources.
				</p>
				{types.length > 0 ? (
					<div className="mt-3 flex flex-wrap items-center gap-1.5">
						{types.map(([t]) => {
							const meta = DOC_TYPE_META[t];
							const Icon = meta.icon;
							return (
								<span
									key={t}
									className="inline-flex items-center gap-1 rounded border border-hairline bg-surface-2 px-1.5 py-0.5 text-[10px] text-ink-subtle"
								>
									<Icon className="size-3" style={{ color: meta.tint }} />
									{meta.label}
								</span>
							);
						})}
					</div>
				) : null}
				<div className="mt-3 flex items-center justify-between border-t border-hairline pt-2.5 text-meta text-ink-subtle">
					<span>
						<b className="font-medium tabular-nums text-ink-muted">{stats?.documentCount ?? 0}</b>{" "}
						docs ·{" "}
						<b className="font-medium tabular-nums text-ink-muted">
							{(stats?.totalChunks ?? 0).toLocaleString()}
						</b>{" "}
						chunks
					</span>
					<span>updated {timeAgo(workspace.updatedAt)}</span>
				</div>
			</div>

			{/* Delete — sits above the overlay link. */}
			<div className="absolute right-2 top-2 opacity-0 transition-opacity group-hover:opacity-100">
				<ConfirmDialog
					title={`Delete "${workspace.name}"?`}
					description="This workspace and all of its documents and indexed chunks will be permanently removed."
					confirmLabel="Delete"
					destructive
					onConfirm={onDelete}
				>
					<button
						type="button"
						aria-label="Delete workspace"
						className="flex size-7 items-center justify-center rounded-md bg-surface-2/80 text-ink-subtle hover:text-destructive"
					>
						<Trash2 className="size-3.5" />
					</button>
				</ConfirmDialog>
			</div>
		</div>
	);
}

export function WorkspacesPage() {
	const { data: workspaces, isLoading } = useWorkspaces();
	const createWorkspace = useCreateWorkspace();
	const deleteWorkspace = useDeleteWorkspace();
	const [name, setName] = useState("");
	const [search, setSearch] = useState("");
	const [creating, setCreating] = useState(false);

	const handleCreate = (e: React.FormEvent) => {
		e.preventDefault();
		const trimmed = name.trim();
		if (trimmed.length === 0 || createWorkspace.isPending) return;
		createWorkspace.mutate(trimmed, {
			onSuccess: () => {
				setName("");
				setCreating(false);
			},
		});
	};

	const filtered = (workspaces ?? []).filter((w) =>
		w.name.toLowerCase().includes(search.trim().toLowerCase()),
	);

	return (
		<>
			<PageHeader
				eyebrow="KNOWLEDGE BASE"
				icon={Library}
				title="Workspaces"
				subtitle="Grouped document sets you can query with grounded Q&A."
				actions={
					<Button onClick={() => setCreating(true)}>
						<Plus className="size-4" />
						New workspace
					</Button>
				}
			/>
			<Toolbar
				left={
					<div className="relative">
						<Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-ink-subtle" />
						<Input
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder="Find a workspace…"
							className="h-7 w-64 pl-7 text-body-sm"
						/>
					</div>
				}
				right={
					<span className="text-meta text-ink-subtle">
						{workspaces?.length ?? 0} workspace{(workspaces?.length ?? 0) === 1 ? "" : "s"}
					</span>
				}
			/>
			<PageBody className="p-6">
				{creating ? (
					<form
						onSubmit={handleCreate}
						className="mb-4 flex items-center gap-2 rounded-lg border border-hairline bg-surface-1 p-3"
					>
						<Input
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="New workspace name…"
							className="flex-1"
						/>
						<Button type="submit" disabled={createWorkspace.isPending || name.trim().length === 0}>
							{createWorkspace.isPending ? "Creating…" : "Create"}
						</Button>
						<Button type="button" variant="ghost" onClick={() => setCreating(false)}>
							Cancel
						</Button>
					</form>
				) : null}

				{isLoading ? (
					<div className="grid grid-cols-3 gap-3">
						{Array.from({ length: 3 }).map((_, i) => (
							// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholder
							<Skeleton key={i} h={180} className="block w-full" />
						))}
					</div>
				) : workspaces && workspaces.length > 0 ? (
					<div className="grid grid-cols-3 gap-3">
						{filtered.map((w) => (
							<WorkspaceCard
								key={w.id}
								workspace={w}
								onDelete={() => deleteWorkspace.mutate(w.id)}
							/>
						))}
						<button
							type="button"
							onClick={() => setCreating(true)}
							className="flex min-h-[180px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-hairline-strong p-4 text-center text-ink-subtle hover:border-primary/50 hover:text-foreground"
						>
							<span className="flex size-9 items-center justify-center rounded-md border border-hairline bg-surface-1">
								<Plus className="size-4" />
							</span>
							<span className="text-body-sm font-medium">New workspace</span>
							<span className="text-meta">Upload docs and start asking</span>
						</button>
					</div>
				) : (
					<EmptyState
						icon={Library}
						title="No workspaces yet"
						body="Create your first workspace, then upload documents and ask questions."
						primary={
							<Button onClick={() => setCreating(true)}>
								<Plus className="size-4" />
								New workspace
							</Button>
						}
					/>
				)}
			</PageBody>
		</>
	);
}
